import type { PresencePeer } from '@forge/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { getSocket, joinRoom, leaveRoom } from './socket';
import type { EntityChanged } from './socket';

/**
 * Joins a realtime room for as long as the component is mounted and exposes the
 * live presence roster for it.
 */
export function useRoom(room: string | null): { peers: PresencePeer[]; socketId: string | null; error: string | null } {
  const [peers, setPeers] = useState<PresencePeer[]>([]);
  const [socketId, setSocketId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!room) {
      setPeers([]);
      setSocketId(null);
      return;
    }

    let active = true;
    const socket = getSocket();

    const onPresence = (payload: { room: string; peers: PresencePeer[] }) => {
      if (payload.room === room) setPeers(payload.peers);
    };
    socket.on('presence', onPresence);

    const join = async () => {
      const result = await joinRoom(room);
      if (!active) return;
      if (result.ok) {
        setPeers(result.peers ?? []);
        setSocketId(result.socketId ?? socket.id ?? null);
        setError(null);
      } else {
        setError(result.error ?? 'Could not join the realtime room');
      }
    };

    void join();
    // A reconnect drops server-side room membership, so re-join.
    socket.on('connect', join);

    return () => {
      active = false;
      socket.off('presence', onPresence);
      socket.off('connect', join);
      leaveRoom(room);
    };
  }, [room]);

  return { peers, socketId, error };
}

/**
 * Keeps react-query caches honest: when someone else changes an entity in this
 * project, invalidate exactly the lists that describe it.
 */
export function useProjectRealtime(projectId: string | undefined): void {
  const qc = useQueryClient();
  const selfRef = useRef<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const socket = getSocket();

    const onEntity = (payload: EntityChanged) => {
      if (payload.projectId !== projectId) return;
      if (payload.actorId === selfRef.current) return;

      const key = ['project', projectId] as const;
      switch (payload.entity) {
        case 'endpoint':
          void qc.invalidateQueries({ queryKey: [...key, 'endpoints'] });
          void qc.invalidateQueries({ queryKey: [...key, 'endpoint', payload.id] });
          break;
        case 'dashboard':
          void qc.invalidateQueries({ queryKey: [...key, 'dashboards'] });
          void qc.invalidateQueries({ queryKey: [...key, 'dashboard', payload.id] });
          break;
        case 'datasource':
          void qc.invalidateQueries({ queryKey: [...key, 'datasources'] });
          break;
        case 'member':
          void qc.invalidateQueries({ queryKey: [...key, 'members'] });
          break;
        case 'token':
          void qc.invalidateQueries({ queryKey: [...key, 'tokens'] });
          break;
        case 'project':
          void qc.invalidateQueries({ queryKey: ['projects'] });
          void qc.invalidateQueries({ queryKey: key });
          break;
      }
      void qc.invalidateQueries({ queryKey: [...key, 'overview'] });
    };

    socket.on('entity:changed', onEntity);
    return () => {
      socket.off('entity:changed', onEntity);
    };
  }, [projectId, qc]);
}

/** Subscribes to the live run-log stream for a project. */
export function useRunLogStream(
  projectId: string | undefined,
  onLog: (log: import('@forge/shared').RunLog) => void,
  enabled = true,
): void {
  const handlerRef = useRef(onLog);
  handlerRef.current = onLog;

  useEffect(() => {
    if (!projectId || !enabled) return;
    const socket = getSocket();
    const listener = (log: import('@forge/shared').RunLog) => {
      if (log.projectId === projectId) handlerRef.current(log);
    };
    socket.on('run:log', listener);
    return () => {
      socket.off('run:log', listener);
    };
  }, [projectId, enabled]);
}
